const EVENT_BINDINGS = [
  { attribute: "onclick", dataAttribute: "data-action-click", eventName: "click", delegated: true },
  { attribute: "onchange", dataAttribute: "data-action-change", eventName: "change", delegated: true },
  { attribute: "oninput", dataAttribute: "data-action-input", eventName: "input", delegated: true },
  { attribute: "onmouseenter", dataAttribute: "data-action-mouseenter", eventName: "mouseenter", delegated: false },
  { attribute: "onmouseleave", dataAttribute: "data-action-mouseleave", eventName: "mouseleave", delegated: false },
  { attribute: "onerror", dataAttribute: "data-action-error", eventName: "error", delegated: false },
];

const EVENT_SELECTORS = EVENT_BINDINGS
  .flatMap(({ attribute, dataAttribute }) => [`[${attribute}]`, `[${dataAttribute}]`])
  .join(",");

function splitTopLevel(input, separator = ",") {
  const parts = [];
  let current = "";
  let quote = "";
  let depth = 0;

  for (let index = 0; index < input.length; index += 1) {
    const char = input[index];
    const prev = input[index - 1];

    if (quote) {
      current += char;
      if (char === quote && prev !== "\\") quote = "";
      continue;
    }

    if (char === "'" || char === '"') {
      quote = char;
      current += char;
      continue;
    }

    if (char === "(" || char === "{" || char === "[") depth += 1;
    if (char === ")" || char === "}" || char === "]") depth -= 1;

    if (char === separator && depth === 0) {
      if (current.trim()) parts.push(current.trim());
      current = "";
      continue;
    }

    current += char;
  }

  if (current.trim()) parts.push(current.trim());
  return parts;
}

function unquote(value) {
  const quote = value[0];
  if ((quote !== "'" && quote !== '"') || value[value.length - 1] !== quote) return value;
  return value
    .slice(1, -1)
    .replace(/\\'/g, "'")
    .replace(/\\"/g, '"')
    .replace(/\\\\/g, "\\");
}

function resolveActionArg(raw, event, element) {
  const value = raw.trim();
  if (!value) return undefined;
  if (/^['"]/.test(value)) return unquote(value);
  if (value === "true") return true;
  if (value === "false") return false;
  if (value === "null") return null;
  if (value === "undefined") return undefined;
  if (/^-?\d+(\.\d+)?$/.test(value)) return Number(value);
  if (value === "event") return event;
  if (value === "this" || value === "element") return element;
  if (value === "this.value") return element.value;
  if (value === "this.checked") return Boolean(element.checked);
  if (value === "this.src") return element.src;
  if (value === "this.alt") return element.alt;
  if (value === "S.currentBizId") return window.S?.currentBizId;

  const datasetMatch = value.match(/^this\.dataset\.([A-Za-z_$][\w$]*)$/);
  if (datasetMatch) return element.dataset?.[datasetMatch[1]];

  if (value === "(getSorted()[0]||BUSINESSES[0]||{}).id") {
    return (window.getSorted?.()[0] || window.BUSINESSES?.[0] || {}).id;
  }

  return value;
}

function runActionStatement(statement, event, element) {
  const code = statement.trim();
  if (!code) return undefined;

  if (code === "event.stopPropagation()") {
    event.stopPropagation();
    return undefined;
  }

  if (code === "this.onerror=null") {
    element.onerror = null;
    return undefined;
  }

  if (code === "this.src=this.dataset.fallback") {
    element.src = element.dataset?.fallback || "";
    return undefined;
  }

  const scrollMatch = code.match(/^document\.getElementById\(['"]([^'"]+)['"]\)\?\.scrollIntoView\((.*)\)$/);
  if (scrollMatch) {
    document.getElementById(scrollMatch[1])?.scrollIntoView({ behavior: "smooth", block: "start" });
    return undefined;
  }

  const callMatch = code.match(/^([A-Za-z_$][\w$]*)\((.*)\)$/);
  if (!callMatch) {
    console.warn("Torqo action was not recognized", code);
    return undefined;
  }

  const fn = window[callMatch[1]];
  if (typeof fn !== "function") {
    console.warn("Torqo action is not available", callMatch[1]);
    return undefined;
  }

  const args = callMatch[2].trim()
    ? splitTopLevel(callMatch[2]).map((arg) => resolveActionArg(arg, event, element))
    : [];
  return fn(...args);
}

function runInlineAction(code, event, element) {
  if (!code) return undefined;
  let result;
  for (const statement of splitTopLevel(code, ";")) {
    result = runActionStatement(statement, event, element);
  }
  return result;
}

function bindAttribute(element, binding) {
  const sourceAttribute = element.hasAttribute(binding.dataAttribute)
    ? binding.dataAttribute
    : binding.attribute;
  const code = element.getAttribute(sourceAttribute);
  if (!code) return;

  if (sourceAttribute === binding.attribute) {
    element.setAttribute(binding.dataAttribute, code);
    element.removeAttribute(binding.attribute);
  }

  if (binding.delegated) return;

  element.dataset[`bound${binding.eventName}`] = "1";
  element.addEventListener(binding.eventName, (event) => runInlineAction(code, event, element));
}

function bindInlineActions(root = document) {
  if (!root) return;
  const elements = [];
  if (root.nodeType === 1) elements.push(root);
  if (root.querySelectorAll) {
    elements.push(...root.querySelectorAll(EVENT_SELECTORS));
  }
  for (const element of elements) {
    for (const binding of EVENT_BINDINGS) {
      if (element.hasAttribute?.(binding.attribute) || element.hasAttribute?.(binding.dataAttribute)) {
        bindAttribute(element, binding);
      }
    }
  }
}

export function initActionBinder() {
  const start = () => {
    bindInlineActions(document);

    for (const binding of EVENT_BINDINGS.filter((item) => item.delegated)) {
      document.addEventListener(binding.eventName, (event) => {
        const target = event.target?.closest?.(`[${binding.dataAttribute}]`);
        if (!target) return;
        runInlineAction(target.getAttribute(binding.dataAttribute), event, target);
      });
    }

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) bindInlineActions(node);
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
}
